import React, { useEffect, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import mfaApiService from 'api/MfaApiService';
import { CircularProgress, Box } from '@mui/material';
import { useSelector } from '../store/hooks';

interface MfaGuardProps {
  children: React.ReactNode;
  customerSlug: string;
}

const MfaGuard: React.FC<MfaGuardProps> = ({ children, customerSlug }) => {
  const [loading, setLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);
  const location = useLocation();
  const appState = useSelector((state) => state.app);

  useEffect(() => {
    const checkMfaStatus = async () => {
      // Skip check if already on MFA settings page
      if (location.pathname.includes('/settings/mfa')) {
        setLoading(false);
        return;
      }

      // Check if MFA check was already done in this session
      const mfaCheckResult = sessionStorage.getItem('mfa_check_result');
      
      if (mfaCheckResult === 'passed') {
        setLoading(false);
        return;
      }

      // Wait for user to be loaded
      if (!appState.user?.id) {
        setLoading(false);
        return;
      }

      try {
        // Check BOTH database (appState.user) and Cognito (API)
        const status = await mfaApiService.getMfaStatus();
        const userMfaEnabled = appState.user.mfa_enabled || false;
        const userMfaSetupCompleted = appState.user.mfa_setup_completed || false;
        
        console.log('MfaGuard check:', {
          fromDatabase: { mfaEnabled: userMfaEnabled, mfaSetupCompleted: userMfaSetupCompleted },
          fromCognito: status
        });
        
        // MFA is required if either database or Cognito says it's enabled, and it's not completed
        const needsSetup = (userMfaEnabled || status.mfaEnabled) && 
                          !userMfaSetupCompleted && 
                          !status.mfaSetupCompleted;
        
        if (needsSetup) {
          setMfaRequired(true);
          sessionStorage.setItem('mfa_check_result', 'required');
        } else {
          sessionStorage.setItem('mfa_check_result', 'passed');
        }
      } catch (error) {
        console.error('Error checking MFA status:', error);
        // On error, fall back to database check only
        if (appState.user.mfa_enabled && !appState.user.mfa_setup_completed) {
          setMfaRequired(true);
          sessionStorage.setItem('mfa_check_result', 'required');
        } else {
          sessionStorage.setItem('mfa_check_result', 'error');
        }
      } finally {
        setLoading(false);
      }
    };

    checkMfaStatus();
  }, [location.pathname, appState.user?.id]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (mfaRequired) {
    return <Redirect to={`/${customerSlug}/settings/mfa`} />;
  }

  return <>{children}</>;
};

export default MfaGuard;

